import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, BarChart3, Loader2 } from 'lucide-react';
import ModelReport from '../components/ModelReport';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting/board/results`;
const pct = (p) => `${Math.round(p * 100)}%`;
const TONE = { WON: 'text-emerald-300', LOST: 'text-rose-300', PENDING: 'text-zinc-400' };

const Calib = ({ title, rows }) => (
  <div className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-3" data-testid={`calib-${title.toLowerCase()}`}>
    <p className="text-sm font-bold text-white mb-1">{title}: are our % picks hitting?</p>
    {!rows.length ? <p className="text-[11px] text-zinc-500">No graded picks yet — this fills in after games finish.</p> : rows.map((r) => (
      <p key={r.range} className="text-xs text-zinc-300">
        {r.range} picks: hit <b className={r.hit_rate >= r.avg_pred - 0.05 ? 'text-emerald-300' : 'text-rose-300'}>{pct(r.hit_rate)}</b> vs predicted {pct(r.avg_pred)} <span className="text-zinc-500">({r.n} picks)</span>
      </p>
    ))}
  </div>
);

export default function ResultsPage() {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(API).then((r) => setData(r.data)).catch(() => setData({ error: true })); }, []);
  if (sessionStorage.getItem('sports_betting_authed') !== 'true') return <Navigate to="/sports-betting" replace />;
  return (
    <div className="min-h-screen bg-zinc-950 text-white p-4 md:p-6" data-testid="results-page">
      <div className="max-w-4xl mx-auto space-y-4">
        <Link to="/sports-betting" className="text-sm text-zinc-400 hover:text-white flex items-center gap-1" data-testid="results-back"><ArrowLeft className="w-4 h-4" /> My tickets</Link>
        <h1 className="text-2xl font-bold flex items-center gap-2"><BarChart3 className="w-6 h-6 text-emerald-400" />Results & Accuracy</h1>
        <ModelReport />
        {!data && <div className="flex gap-2 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" />Grading…</div>}
        {data?.error && <p className="text-sm text-red-400">Could not load results.</p>}
        {data?.weeks && (
          <>
            <p className="text-[11px] text-zinc-500" data-testid="results-mins">Current minimums: NFL legs {pct(data.mins.nfl)}+, College legs {pct(data.mins.cfb)}+, NBA legs {pct(data.mins.nba || 0.75)}+. These rise automatically if picks hit less often than predicted.</p>
            {data.tighten_log?.map((t) => <p key={t.at} className="text-[11px] text-amber-300">Raised {t.league.toUpperCase()} minimum to {pct(t.to)} — {t.why}</p>)}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Calib title="NFL" rows={data.calibration.nfl} />
              <Calib title="College" rows={data.calibration.cfb} />
              <Calib title="NBA" rows={data.calibration.nba || []} />
            </div>
            {data.weeks.map((w) => (
              <div key={`${w.league}-${w.week}`} className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-3" data-testid={`results-week-${w.league}-${w.week}`}>
                <p className="text-sm font-bold text-white">{w.league === 'nfl' ? 'NFL Week' : w.league === 'nba' ? 'NBA' : 'College Week'} {w.week} <span className="text-zinc-400 font-normal">· {w.won} won · {w.lost} lost{w.frozen ? '' : ' · tickets still updating until kickoff'}</span></p>
                {w.tickets.map((t) => (
                  <p key={t.n} className="text-xs text-zinc-300">Safe {t.n} ({t.rating}, {pct(t.all_hit)}) — {t.hits}/{t.legs} legs hit · <b className={TONE[t.status]}>{t.status}</b></p>
                ))}
              </div>
            ))}
            {!data.weeks.length && <p className="text-xs text-zinc-500">No weeks recorded yet.</p>}
          </>
        )}
      </div>
    </div>
  );
}
